// Word/Outlook's floating "Paste Options" smart tag — appears right after
// a native Ctrl+V, anchored just past the pasted content, letting the user
// retroactively re-apply a different paste mode to exactly what was just
// pasted (without needing to have chosen a mode up front via the ribbon's
// Paste Special menu). Mirrors Word's own widget: a small clipboard button
// that expands into the 4 mode icons plus a "Set Default Paste..." link
// that changes what plain Ctrl+V does going forward, app-wide.
//
// Lives in its own file (rather than inline in RichTextEditor.js) since
// it's a self-contained hook + portal-rendered UI, following the same
// portal pattern WordRibbon.js's dropdowns already use to escape any
// ribbon-bar ancestor's `overflow` clipping.
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cleanPastedHtml, escapeHtml, insertCleanedHtml, getDefaultPasteMode, setDefaultPasteMode } from "./pasteClean";

const MODES = [
  { id: "keep", label: "Keep Source Formatting", glyph: "K" },
  { id: "merge", label: "Merge Formatting", glyph: "M" },
  { id: "match", label: "Match Destination Formatting", glyph: "D" },
  { id: "text", label: "Keep Text Only", glyph: "A" },
];

// `getQuill` — function returning the live Quill instance (same pattern
// used throughout WordRibbon.js). Returns `{ show(rawHtml, range), node }`
// — call `show` right after a native paste completes; render `node`
// (already portaled — safe to include unconditionally in the tree, it's
// `null` when hidden).
export function usePasteOptionsWidget(getQuill) {
  const [paste, setPaste] = useState(null); // {rawHtml, range, activeMode} — no position baked in
  const [pos, setPos] = useState(null); // {top, left} — recomputed continuously, see below
  const [menuOpen, setMenuOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const rootRef = useRef(null);
  // While true, our OWN re-clean (applyMode) is mutating the document —
  // the dismiss-on-text-change listener below must ignore that particular
  // change instead of immediately closing the widget it's part of.
  const suppressRef = useRef(false);

  const dismiss = useCallback(() => {
    setPaste(null);
    setPos(null);
    setMenuOpen(false);
    setSettingsOpen(false);
  }, []);

  const show = useCallback((rawHtml, range) => {
    if (!range) return;
    setPaste({ rawHtml, range, activeMode: getDefaultPasteMode() });
    setMenuOpen(false);
    setSettingsOpen(false);
  }, []);

  // `position:fixed` alone is viewport-relative — it does NOT move with
  // the page. Without this, the button freezes at wherever it was first
  // computed: scroll the page (or an inner ribbon-wrapper's own
  // `overflow:auto` region — the same ancestor-scroll situation the
  // dropdown-clipping fix in WordRibbon.js's `useFlyoutPosition` already
  // handles) even a little and the button stops tracking the pasted text
  // entirely, which is exactly "button doesn't move when I scroll" /
  // "doesn't show after scrolling down to a large paste". Re-measuring on
  // every scroll/resize (capture:true on window catches scroll events
  // from ANY nested scrollable ancestor, not just the window itself, the
  // same technique `useFlyoutPosition` already relies on) keeps it glued
  // to the actual end of the pasted content no matter what scrolls.
  useEffect(() => {
    if (!paste) return undefined;
    const quill = getQuill();
    if (!quill) return undefined;
    const update = () => {
      try {
        const anchor = Math.max(paste.range.index + paste.range.length - 1, paste.range.index);
        const bounds = quill.getBounds(anchor);
        if (!bounds) { setPos(null); return; }
        // quill.getBounds() returns coordinates straight from the native
        // Range.getBoundingClientRect()/Element.getBoundingClientRect()
        // APIs (see node_modules/quill/core/selection.js) — these are
        // ALREADY viewport-absolute and already account for every
        // ancestor's current scroll position, including .ql-editor's own
        // internal scroll (which is what actually scrolls for a paste
        // long enough to exceed the editor's own fixed height). Adding
        // the editor container's own getBoundingClientRect() on top of
        // this — the original bug here — double-counts that offset and
        // pushes the widget increasingly far off-screen the further the
        // pasted content sits from the editor's top, which is exactly why
        // large pastes made the button disappear off-screen while small
        // ones were only slightly (and easy-to-miss-ly) mispositioned.
        setPos({ top: bounds.bottom + 4, left: bounds.left });
      } catch (e) {
        setPos(null); // couldn't compute a position right now — hide until the next successful measurement rather than mis-place it
      }
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [paste, getQuill]);

  // Dismiss the moment the user does anything else — types, clicks
  // elsewhere — exactly like Word's own paste-options tag.
  useEffect(() => {
    if (!paste) return undefined;
    const quill = getQuill();
    if (!quill) return undefined;
    const onTextChange = () => { if (!suppressRef.current) dismiss(); };
    const onMouseDown = (e) => {
      if (rootRef.current && rootRef.current.contains(e.target)) return;
      dismiss();
    };
    quill.on("text-change", onTextChange);
    document.addEventListener("mousedown", onMouseDown, true);
    return () => {
      try { quill.off("text-change", onTextChange); } catch (e) { /* ignore */ }
      document.removeEventListener("mousedown", onMouseDown, true);
    };
  }, [paste, getQuill, dismiss]);

  const applyMode = useCallback((mode) => {
    const quill = getQuill();
    if (!quill || !paste) return;
    suppressRef.current = true;
    try {
      const cleaned = (mode === "text" || !paste.rawHtml)
        ? escapeHtml(quill.getText(paste.range.index, paste.range.length)).replace(/\n/g, "<br>")
        : cleanPastedHtml(paste.rawHtml, mode);
      const newRange = insertCleanedHtml(quill, cleaned, paste.range);
      setPaste(p => (p ? { ...p, range: newRange, activeMode: mode } : p));
    } finally {
      suppressRef.current = false;
    }
    setMenuOpen(false);
  }, [getQuill, paste]);

  const node = (paste && pos) ? createPortal(
    <div ref={rootRef} style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 10000, fontFamily: "inherit" }}>
      <button type="button" title="Paste Options" onMouseDown={e => e.preventDefault()} onClick={() => setMenuOpen(o => !o)}
        style={{ display: "flex", alignItems: "center", gap: 2, background: "#fff", border: "1px solid #d1cec9", borderRadius: 4, boxShadow: "0 2px 8px rgba(16,24,40,.18)", padding: "3px 5px", cursor: "pointer" }}>
        <span style={{ fontSize: 13, lineHeight: 1 }}>📋</span>
        <span style={{ fontSize: 9, color: "#8890B0" }}>▾</span>
      </button>
      {menuOpen && (
        <div style={{ marginTop: 4, background: "#fff", border: "1px solid #d1cec9", borderRadius: 6, boxShadow: "0 8px 24px rgba(16,24,40,.16)", padding: 8, width: 226 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#201F1E", marginBottom: 6 }}>Paste Options:</div>
          <div style={{ display: "flex", gap: 4, marginBottom: 8 }}>
            {MODES.map(m => (
              <button key={m.id} type="button" title={m.label} onMouseDown={e => e.preventDefault()} onClick={() => applyMode(m.id)}
                style={{
                  width: 32, height: 26, display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: 12, color: "#201F1E", cursor: "pointer", borderRadius: 4,
                  background: paste.activeMode === m.id ? "#DEECF9" : "#fff",
                  border: "1px solid " + (paste.activeMode === m.id ? "#0078D4" : "#d1cec9"),
                }}>
                {m.glyph}
              </button>
            ))}
          </div>
          <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => setSettingsOpen(o => !o)}
            style={{ fontSize: 11, color: "#0078D4", background: "none", border: "none", padding: 0, cursor: "pointer", textDecoration: "underline" }}>
            Set Default Paste…
          </button>
          {settingsOpen && (
            <div style={{ marginTop: 8, borderTop: "1px solid #e2e6f0", paddingTop: 8 }}>
              <div style={{ fontSize: 10.5, color: "#8890B0", marginBottom: 4 }}>Default for Ctrl+V:</div>
              {MODES.map(m => (
                <label key={m.id} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11.5, padding: "3px 2px", cursor: "pointer" }}>
                  <input
                    type="radio"
                    name="sdr-default-paste-mode"
                    checked={getDefaultPasteMode() === m.id}
                    onChange={() => { setDefaultPasteMode(m.id); dismiss(); }}
                  />
                  {m.label}
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>,
    document.body
  ) : null;

  return { show, node };
}
